import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import ShopvibesSettings from "@/src/components/shopvibes/ShopvibesSettings.tsx";
import { shopvibesKeys } from "@/src/hooks/useShopvibes.ts";
import { invalidateShopvibesCatalog } from "@/src/services/shopvibes/catalogClient.ts";
import { trackShopVibesAccountConnected } from "@/src/services/analyticsService";
import { AppError } from "@/src/utils/appError";
import { resolveErrorMessage } from "@/src/utils/errorResolver";

interface ShopvibesSettingsSearch {
  shopvibes?: "connected" | "error";
  code?: string;
}

const ShopvibesSettingsRoute: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  // Where the OAuth flow lands: `/shopvibes/callback` finalizes the link and
  // forwards the outcome here as a search param.
  React.useEffect(() => {
    if (!search.shopvibes) return;

    if (search.shopvibes === "connected") {
      toast.success(t("shopvibes.settings.connectedToast"));
      trackShopVibesAccountConnected();
      // A fresh link may be a different Shopvibes account, so the catalog
      // cached outside React Query has to go too.
      invalidateShopvibesCatalog();
      void queryClient.invalidateQueries({ queryKey: shopvibesKeys.all });
    } else {
      toast.error(
        resolveErrorMessage(
          new AppError(
            search.code || "shopvibes/unknown",
            "Shopvibes OAuth callback returned an error"
          ),
          t
        )
      );
    }

    // The param has done its job; clearing it keeps a reload or a
    // back-navigation from re-showing the toast.
    navigate({ to: "/settings/shopvibes", search: {}, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.shopvibes, search.code]);

  return <ShopvibesSettings />;
};

export const Route = createFileRoute("/settings/shopvibes")({
  component: ShopvibesSettingsRoute,
  validateSearch: (
    search: Record<string, unknown>
  ): ShopvibesSettingsSearch => ({
    shopvibes:
      search.shopvibes === "connected" || search.shopvibes === "error"
        ? search.shopvibes
        : undefined,
    code: typeof search.code === "string" ? search.code : undefined,
  }),
});
